import React, { useEffect, useMemo, useState } from 'react';
import { colors, borders, fonts, ScoreColor } from './tokens';

export interface MonthSummary {
    idx: number;
    theme: string;
    done: number;
    total: number;
    status: 'locked' | 'active' | 'done';
}

export interface TaskItem {
    n: number;
    title: string;
    tag: string;
    pts: number;
    /** Compact time estimate, e.g. "5 MIN", "30 MIN", "<1 MIN". Defaults to "5 MIN" if omitted. */
    time?: string;
    /** Full time label, e.g. "3 days, 2 minutes per tagging" - shown when expanded. */
    timeFull?: string;
    status: 'done' | 'today' | 'queued' | 'locked';
    description?: string;
    howSteps?: string[];
    why?: string;
    contributions?: { dim: string; pts: number; color: string }[];
    dimensionPoints?: Record<string, number>;
    patternPoints?: Record<string, number>;
    pendulumPoints?: Record<string, number>;
}

export interface TaskSortOption {
    id: string;
    label: string;
    color: string;
}

interface TaskListProps {
    months: MonthSummary[];
    activeMonthTasks: TaskItem[];
    score: ScoreColor;
    headerTitle?: string;
    headerSubtitle?: string;
    onMarkDone?: (taskN: number) => void;
    sortOptions?: TaskSortOption[];
    sortDimension?: string | null;
    onSortDimensionChange?: (dimensionId: string | null) => void;
    patternSortOptions?: TaskSortOption[];
    sortPattern?: string | null;
    onSortPatternChange?: (patternId: string | null) => void;
    pendulumSortOptions?: TaskSortOption[];
    sortPendulum?: string | null;
    onSortPendulumChange?: (pendulumId: string | null) => void;
    sortLabel?: string;
    recommendedLabel?: string;
    patternSortLabel?: string;
    pendulumSortLabel?: string;
    sortHint?: string;
}

const LockIcon: React.FC<{ size?: number; color: string }> = ({ size = 11, color }) => (
    <svg width={size} height={size} viewBox="0 0 12 12" fill="none">
        <rect x="2.5" y="5.5" width="7" height="5" rx="0.8" stroke={color} strokeWidth="1" fill="none" />
        <path d="M4 5.5V3.8a2 2 0 1 1 4 0V5.5" stroke={color} strokeWidth="1" fill="none" strokeLinecap="round" />
    </svg>
);

const MonthChip: React.FC<{
    m: MonthSummary;
    selected: boolean;
    score: ScoreColor;
}> = ({ m, selected, score }) => {
    const isActive = m.status === 'active';
    const isDone = m.status === 'done';
    const isLocked = m.status === 'locked';
    let dotColor = 'rgba(255,255,255,0.18)';

    if (isDone) dotColor = '#84cc16';
    else if (isActive) dotColor = score.hex;

    return (
        <div
            style={{
                flex: 1,
                padding: '14px 14px 12px',
                background: colors.cardBgInner,
                border: selected ? '1px solid rgba(255,255,255,0.18)' : borders.hair2,
                borderRadius: 10,
                opacity: isLocked ? 0.55 : 1,
                cursor: isLocked ? 'default' : 'pointer',
                transition: 'border-color .15s, background .15s',
                position: 'relative',
                minWidth: 0,
            }}
        >
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: 14,
                }}
            >
                <span
                    style={{
                        fontSize: 11,
                        color: selected ? colors.textPrimary : colors.muted,
                        fontFamily: fonts.mono,
                        letterSpacing: '0.18em',
                        fontWeight: 500,
                    }}
                >
                    MONTH {String(m.idx).padStart(2, '0')}
                </span>
                {isLocked && <LockIcon size={11} color={colors.subtle} />}
            </div>
            <div
                style={{
                    height: 2,
                    background: 'rgba(255,255,255,0.05)',
                    position: 'relative',
                }}
            >
                <div
                    style={{
                        position: 'absolute',
                        inset: 0,
                        width: `${(m.done / m.total) * 100}%`,
                        background: dotColor,
                        height: 2,
                    }}
                />
            </div>
            <div
                style={{
                    marginTop: 8,
                    fontSize: 9,
                    color: colors.subtle,
                    fontFamily: fonts.mono,
                    letterSpacing: '0.05em',
                    display: 'flex',
                    justifyContent: 'space-between',
                }}
            >
                <span>
                    {m.done}/{m.total}
                </span>
                {isLocked && <span style={{ letterSpacing: '0.15em' }}>LOCKED</span>}
                {isActive && <span style={{ color: score.hex, letterSpacing: '0.15em' }}>ACTIVE</span>}
                {isDone && <span style={{ color: '#84cc16', letterSpacing: '0.15em' }}>DONE</span>}
            </div>
        </div>
    );
};

// Compact row for completed tasks shown at the bottom of the list (in completion order).
// Same dense layout as queued tasks would have if collapsed, but with ✓, line-through, and dimmed.
const DoneTaskRow: React.FC<{ t: TaskItem; isLast: boolean }> = ({ t, isLast }) => (
    <div
        className="dash-edit__done-row"
        style={{
            padding: '12px 28px',
            borderBottom: isLast ? 'none' : borders.hair2,
            display: 'grid',
            gridTemplateColumns: '40px minmax(0, 1fr) auto auto',
            alignItems: 'center',
            gap: 16,
            opacity: 0.55,
        }}
    >
        <div style={{ fontSize: 11, color: colors.subtle, fontFamily: fonts.mono, letterSpacing: '0.05em' }}>
            {String(t.n).padStart(2, '0')}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            <div
                style={{
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    border: '1.5px solid #84cc16',
                    display: 'grid',
                    placeItems: 'center',
                    fontSize: 9,
                    color: '#84cc16',
                    flexShrink: 0,
                }}
            >
                ✓
            </div>
            <span
                style={{
                    fontSize: 13,
                    color: colors.muted,
                    textDecoration: 'line-through',
                    letterSpacing: '-0.005em',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }}
            >
                {t.title}
            </span>
            <span
                style={{
                    fontSize: 9,
                    color: colors.subtle,
                    padding: '3px 8px',
                    border: borders.hair2,
                    borderRadius: 999,
                    fontFamily: fonts.mono,
                    letterSpacing: '0.1em',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                }}
            >
                {t.tag.toUpperCase()}
            </span>
        </div>
        <div style={{ fontSize: 12, color: '#84cc16', fontFamily: fonts.mono, letterSpacing: '-0.02em' }}>+{t.pts}</div>
        <div style={{ color: colors.subtle, fontSize: 14 }}>›</div>
    </div>
);

const ContribChip: React.FC<{
    label: string;
    pts: number;
    color: string;
    small?: boolean;
}> = ({ label, pts, color, small }) => (
    <span
        style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: small ? 6 : 8,
            fontSize: small ? 11 : 12,
            color: colors.textPrimary,
            background: colors.pillBg,
            border: `1px solid ${color}33`,
            padding: small ? '4px 10px' : '6px 12px',
            borderRadius: 999,
            fontWeight: 500,
        }}
    >
        <span
            style={{
                width: small ? 5 : 6,
                height: small ? 5 : 6,
                borderRadius: '50%',
                background: color,
                boxShadow: `0 0 4px ${color}aa`,
            }}
        />
        {label}
        <span
            style={{
                color,
                fontFamily: fonts.mono,
                fontSize: small ? 10 : 11,
                letterSpacing: '-0.02em',
            }}
        >
            +{pts} pts
        </span>
    </span>
);

const TodayTaskDetail: React.FC<{
    t: TaskItem;
    score: ScoreColor;
    completing: boolean;
    onMarkDone: (n: number) => void;
}> = ({ t, score, completing, onMarkDone }) => {
    const [expanded, setExpanded] = useState(false);

    return (
        <div
            style={{
                padding: '24px 28px 22px',
                background: colors.cardBgInner,
                borderTop: `2px solid ${score.hex}`,
                borderBottom: borders.hair2,
                position: 'relative',
            }}
        >
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 14,
                    fontSize: 10,
                    fontFamily: fonts.mono,
                    letterSpacing: '0.15em',
                    color: colors.muted,
                    marginBottom: 14,
                }}
            >
                <span style={{ color: score.hex }}>● TODAY</span>
                <span style={{ color: colors.subtle }}>·</span>
                <span>{t.time || '5 MIN'}</span>
                <span style={{ color: colors.subtle }}>·</span>
                <span>{t.tag.toUpperCase()}</span>
                <div style={{ flex: 1 }} />
                <span style={{ fontSize: 11, color: score.hex, letterSpacing: '-0.02em' }}>+{t.pts} PTS</span>
            </div>

            <h3
                style={{
                    fontSize: 22,
                    fontWeight: 500,
                    letterSpacing: '-0.02em',
                    color: colors.textPrimary,
                    margin: '0 0 12px',
                    lineHeight: 1.25,
                    maxWidth: 820,
                }}
            >
                {t.title}
            </h3>

            {t.description && (
                <p
                    style={{
                        margin: 0,
                        fontSize: 14,
                        lineHeight: 1.6,
                        color: colors.muted,
                        maxWidth: 720,
                        letterSpacing: '-0.005em',
                    }}
                >
                    {t.description}
                </p>
            )}

            {t.contributions && t.contributions.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 16 }}>
                    {t.contributions.map((c) => (
                        <ContribChip key={c.dim} label={c.dim} pts={c.pts} color={c.color} />
                    ))}
                </div>
            )}

            {expanded && (t.howSteps?.length || t.why) && (
                <div style={{ marginTop: 20, paddingTop: 18, borderTop: borders.hair2 }}>
                    {t.howSteps && t.howSteps.length > 0 && (
                        <div
                            className="dash-edit__label-row"
                            style={{
                                display: 'grid',
                                gridTemplateColumns: '120px 1fr',
                                gap: 32,
                                marginBottom: 14,
                            }}
                        >
                            <div
                                style={{
                                    fontSize: 10,
                                    fontFamily: fonts.mono,
                                    letterSpacing: '0.18em',
                                    color: colors.subtle,
                                    paddingTop: 2,
                                }}
                            >
                                HOW TO DO
                            </div>
                            <ol
                                style={{
                                    margin: 0,
                                    padding: 0,
                                    listStylePosition: 'inside',
                                    fontSize: 13,
                                    lineHeight: 1.7,
                                    color: '#d8d6d0',
                                    letterSpacing: '-0.005em',
                                }}
                            >
                                {t.howSteps.map((step) => (
                                    <li key={step}>{step}</li>
                                ))}
                            </ol>
                        </div>
                    )}
                    {t.why && (
                        <div
                            className="dash-edit__label-row"
                            style={{
                                display: 'grid',
                                gridTemplateColumns: '120px 1fr',
                                gap: 32,
                            }}
                        >
                            <div
                                style={{
                                    fontSize: 10,
                                    fontFamily: fonts.mono,
                                    letterSpacing: '0.18em',
                                    color: colors.subtle,
                                    paddingTop: 2,
                                }}
                            >
                                WHY THIS WORKS
                            </div>
                            <p
                                style={{
                                    margin: 0,
                                    fontSize: 13,
                                    lineHeight: 1.7,
                                    color: '#d8d6d0',
                                    maxWidth: 720,
                                    letterSpacing: '-0.005em',
                                }}
                            >
                                {t.why}
                            </p>
                        </div>
                    )}
                </div>
            )}

            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    marginTop: 22,
                }}
            >
                {expanded && (
                    <button
                        type="button"
                        onClick={() => onMarkDone(t.n)}
                        style={{
                            background: '#f4f2ee',
                            color: '#0a0a0a',
                            border: '1px solid #f4f2ee',
                            padding: '8px 16px',
                            borderRadius: 999,
                            fontSize: 12,
                            fontWeight: 500,
                            letterSpacing: '-0.005em',
                            cursor: 'pointer',
                            fontFamily: 'inherit',
                        }}
                    >
                        Mark as done
                    </button>
                )}
                <button
                    type="button"
                    onClick={() => setExpanded((e) => !e)}
                    style={{
                        background: 'transparent',
                        color: colors.muted,
                        border: borders.hairline,
                        padding: '8px 16px',
                        borderRadius: 999,
                        fontSize: 12,
                        fontWeight: 500,
                        letterSpacing: '-0.005em',
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                    }}
                >
                    {expanded ? 'Show less' : 'Read more'}
                </button>
            </div>
        </div>
    );
};

const QueuedTaskCard: React.FC<{
    t: TaskItem;
    isLast: boolean;
    completing: boolean;
    onMarkDone: (n: number) => void;
}> = ({ t, isLast, completing, onMarkDone }) => {
    const [expanded, setExpanded] = useState(false);

    return (
        <div
            style={{
                padding: '20px 28px',
                borderBottom: isLast ? 'none' : borders.hair2,
                cursor: 'pointer',
            }}
        >
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 14,
                    fontSize: 10,
                    fontFamily: fonts.mono,
                    letterSpacing: '0.15em',
                    color: colors.muted,
                    marginBottom: 10,
                }}
            >
                <span style={{ color: colors.subtle }}>{String(t.n).padStart(2, '0')}</span>
                <span style={{ color: colors.subtle }}>·</span>
                <span>{t.time || '5 MIN'}</span>
                <span style={{ color: colors.subtle }}>·</span>
                <span>{t.tag.toUpperCase()}</span>
                <div style={{ flex: 1 }} />
                <span
                    style={{
                        fontSize: 11,
                        color: colors.muted,
                        letterSpacing: '-0.02em',
                    }}
                >
                    +{t.pts} PTS
                </span>
            </div>

            <h4
                style={{
                    fontSize: 17,
                    fontWeight: 500,
                    letterSpacing: '-0.015em',
                    color: colors.text,
                    margin: '0 0 8px',
                    lineHeight: 1.3,
                    maxWidth: 820,
                }}
            >
                {t.title}
            </h4>

            {t.description && (
                <p
                    style={{
                        margin: 0,
                        fontSize: 13,
                        lineHeight: 1.6,
                        color: colors.muted,
                        maxWidth: 720,
                        letterSpacing: '-0.005em',
                    }}
                >
                    {t.description}
                </p>
            )}

            {t.contributions && t.contributions.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 12 }}>
                    {t.contributions.map((c) => (
                        <ContribChip key={c.dim} label={c.dim} pts={c.pts} color={c.color} small />
                    ))}
                </div>
            )}

            {expanded && (t.howSteps?.length || t.why) && (
                <div style={{ marginTop: 16, paddingTop: 14, borderTop: borders.hair2 }}>
                    {t.howSteps && t.howSteps.length > 0 && (
                        <div
                            className="dash-edit__label-row"
                            style={{
                                display: 'grid',
                                gridTemplateColumns: '110px 1fr',
                                gap: 24,
                                marginBottom: 12,
                            }}
                        >
                            <div
                                style={{
                                    fontSize: 10,
                                    fontFamily: fonts.mono,
                                    letterSpacing: '0.18em',
                                    color: colors.subtle,
                                    paddingTop: 2,
                                }}
                            >
                                HOW TO DO
                            </div>
                            <ol
                                style={{
                                    margin: 0,
                                    padding: 0,
                                    listStylePosition: 'inside',
                                    fontSize: 12.5,
                                    lineHeight: 1.7,
                                    color: '#d8d6d0',
                                    letterSpacing: '-0.005em',
                                }}
                            >
                                {t.howSteps.map((step) => (
                                    <li key={step}>{step}</li>
                                ))}
                            </ol>
                        </div>
                    )}
                    {t.why && (
                        <div
                            className="dash-edit__label-row"
                            style={{
                                display: 'grid',
                                gridTemplateColumns: '110px 1fr',
                                gap: 24,
                            }}
                        >
                            <div
                                style={{
                                    fontSize: 10,
                                    fontFamily: fonts.mono,
                                    letterSpacing: '0.18em',
                                    color: colors.subtle,
                                    paddingTop: 2,
                                }}
                            >
                                WHY THIS WORKS
                            </div>
                            <p
                                style={{
                                    margin: 0,
                                    fontSize: 12.5,
                                    lineHeight: 1.7,
                                    color: '#d8d6d0',
                                    maxWidth: 720,
                                    letterSpacing: '-0.005em',
                                }}
                            >
                                {t.why}
                            </p>
                        </div>
                    )}
                </div>
            )}

            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    marginTop: 14,
                }}
            >
                {expanded && (
                    <button
                        type="button"
                        onClick={() => onMarkDone(t.n)}
                        style={{
                            background: '#f4f2ee',
                            color: '#0a0a0a',
                            border: 'none',
                            padding: '9px 18px',
                            borderRadius: 999,
                            fontSize: 12,
                            fontWeight: 500,
                            letterSpacing: '-0.005em',
                            cursor: 'pointer',
                            fontFamily: 'inherit',
                        }}
                    >
                        Mark as done
                    </button>
                )}
                <button
                    type="button"
                    onClick={() => setExpanded((e) => !e)}
                    style={{
                        background: 'transparent',
                        color: colors.muted,
                        border: borders.hairline,
                        padding: '7px 14px',
                        borderRadius: 999,
                        fontSize: 12,
                        fontWeight: 500,
                        letterSpacing: '-0.005em',
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                    }}
                >
                    {expanded ? 'Show less' : 'Read more'}
                </button>
            </div>
        </div>
    );
};

const TaskList: React.FC<TaskListProps> = ({
    months,
    activeMonthTasks,
    score,
    headerTitle = 'Your 12-month cycle',
    headerSubtitle = '240 task slots - 20 per month. Each month unlocks after the previous is finished. Behavior changes through repetition: the same techniques recur deliberately.',
    onMarkDone,
    sortOptions = [],
    sortDimension = null,
    onSortDimensionChange,
    patternSortOptions = [],
    sortPattern = null,
    onSortPatternChange,
    pendulumSortOptions = [],
    sortPendulum = null,
    onSortPendulumChange,
    sortLabel = 'SORT EXERCISES',
    recommendedLabel = 'Recommended',
    patternSortLabel = 'Pattern',
    pendulumSortLabel = 'Pendulum',
    sortHint,
}) => {
    const activeMonth = months.find((m) => m.status === 'active') || months[0];
    const preferredTodayN = sortDimension || sortPattern || sortPendulum
        ? activeMonthTasks.find((t) => t.status === 'today' || t.status === 'queued')?.n
        : activeMonthTasks.find((t) => t.status === 'today')?.n;

    // Ordered list of taskN values completed during this session, in completion
    // order. We use an array (not a Set) so completed tasks render at the
    // bottom in the order the user finished them, instead of disappearing.
    const [sessionCompleted, setSessionCompleted] = useState<number[]>([]);
    const completedSet = useMemo(() => new Set(sessionCompleted), [sessionCompleted]);
    const [currentTodayN, setCurrentTodayN] = useState<number | null>(preferredTodayN ?? null);

    useEffect(() => {
        setCurrentTodayN(preferredTodayN ?? null);
    }, [sortDimension, sortPattern, sortPendulum, activeMonth?.idx, preferredTodayN]);

    const handleMarkDone = (taskN: number) => {
        if (completedSet.has(taskN)) return;
        setSessionCompleted((prev) => (prev.includes(taskN) ? prev : [...prev, taskN]));

        if (taskN === currentTodayN) {
            const remainingQueue = activeMonthTasks.filter(
                (t) => t.status === 'queued' && !completedSet.has(t.n) && t.n !== taskN,
            );

            setCurrentTodayN(remainingQueue.length > 0 ? remainingQueue[0].n : null);
        }
        onMarkDone?.(taskN);
    };

    const baseDoneCount = activeMonthTasks.filter((t) => t.status === 'done').length;
    const doneCount = baseDoneCount + sessionCompleted.length;
    const todayTask =
        currentTodayN != null && !completedSet.has(currentTodayN)
            ? activeMonthTasks.find((t) => t.n === currentTodayN)
            : null;
    const queued = activeMonthTasks.filter(
        (t) => (t.status === 'queued' || t.status === 'today') && !completedSet.has(t.n) && t.n !== currentTodayN,
    );
    // Just-completed tasks rendered at the bottom in completion order.
    const justDone = sessionCompleted
        .map((n) => activeMonthTasks.find((t) => t.n === n))
        .filter((t): t is TaskItem => Boolean(t));

    const liveDone = activeMonth ? activeMonth.done + sessionCompleted.length : 0;
    const totalDone = useMemo(
        () => months.reduce((s, m) => s + m.done, 0) + sessionCompleted.length,
        [months, sessionCompleted],
    );
    const totalSlots = months.reduce((s, m) => s + m.total, 0);

    return (
        <div
            className="dash-edit__task-shell"
            style={{
                width: '100%',
                background: colors.bg,
                color: colors.text,
                fontFamily: fonts.display,
                padding: '36px 56px 56px',
            }}
        >
            <div
                className="dash-edit__section-head"
                style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    justifyContent: 'space-between',
                    marginBottom: 24,
                }}
            >
                <div>
                    <h2
                        style={{
                            fontSize: 28,
                            fontWeight: 400,
                            letterSpacing: '-0.02em',
                            margin: 0,
                        }}
                    >
                        {headerTitle}
                    </h2>
                    <p
                        style={{
                            fontSize: 14,
                            color: colors.muted,
                            margin: '6px 0 0',
                            maxWidth: 620,
                            lineHeight: 1.5,
                        }}
                    >
                        {headerSubtitle}
                    </p>
                </div>
                <div style={{ textAlign: 'right' }}>
                    <div
                        style={{
                            fontSize: 24,
                            fontWeight: 300,
                            color: colors.textPrimary,
                            letterSpacing: '-0.02em',
                        }}
                    >
                        {totalDone}
                        <span style={{ color: colors.muted }}>/{totalSlots}</span>
                    </div>
                    <div
                        style={{
                            fontSize: 11,
                            color: colors.muted,
                            fontFamily: fonts.mono,
                            letterSpacing: '0.1em',
                            marginTop: 2,
                        }}
                    >
                        {Math.round((totalDone / Math.max(totalSlots, 1)) * 100)}% COMPLETE
                    </div>
                </div>
            </div>

            <div className="dash-edit__month-chips" style={{ display: 'flex', gap: 6, marginBottom: 28 }}>
                {months.map((m) => (
                    <MonthChip key={m.idx} m={m} selected={m.idx === activeMonth?.idx} score={score} />
                ))}
            </div>

            {sortOptions.length > 0 && (
                <div
                    id="tasks-sort-controls"
                    className="dash-edit__task-sort"
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        padding: '12px 14px',
                        marginBottom: 14,
                        background: colors.cardBgInner,
                        border: borders.hair2,
                        borderRadius: 12,
                    }}
                >
                    <div
                        style={{
                            flexShrink: 0,
                            fontSize: 10,
                            color: colors.subtle,
                            fontFamily: fonts.mono,
                            letterSpacing: '0.16em',
                        }}
                    >
                        {sortLabel}
                    </div>
                    <div
                        className="dash-edit__task-sort-options"
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                            flexWrap: 'wrap',
                        }}
                    >
                        <button
                            type="button"
                            onClick={() => {
                                onSortDimensionChange?.(null);
                                onSortPatternChange?.(null);
                                onSortPendulumChange?.(null);
                            }}
                            aria-pressed={!sortDimension && !sortPattern && !sortPendulum}
                            style={{
                                border:
                                    !sortDimension && !sortPattern && !sortPendulum
                                        ? '1px solid rgba(255,255,255,0.24)'
                                        : borders.hair2,
                                background:
                                    !sortDimension && !sortPattern && !sortPendulum
                                        ? 'rgba(255,255,255,0.08)'
                                        : 'transparent',
                                color:
                                    !sortDimension && !sortPattern && !sortPendulum
                                        ? colors.textPrimary
                                        : colors.muted,
                                borderRadius: 999,
                                padding: '7px 12px',
                                fontSize: 11,
                                fontFamily: 'inherit',
                                cursor: 'pointer',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {recommendedLabel}
                        </button>
                        {sortOptions.map((option) => {
                            const active = sortDimension === option.id;

                            return (
                                <button
                                    key={option.id}
                                    type="button"
                                    onClick={() => {
                                        onSortPatternChange?.(null);
                                        onSortPendulumChange?.(null);
                                        onSortDimensionChange?.(option.id);
                                    }}
                                    aria-pressed={active}
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 7,
                                        border: `1px solid ${active ? `${option.color}88` : 'rgba(255,255,255,0.08)'}`,
                                        background: active ? `${option.color}18` : 'transparent',
                                        color: active ? colors.textPrimary : colors.muted,
                                        borderRadius: 999,
                                        padding: '7px 12px',
                                        fontSize: 11,
                                        fontFamily: 'inherit',
                                        cursor: 'pointer',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    <span
                                        style={{
                                            width: 5,
                                            height: 5,
                                            borderRadius: '50%',
                                            background: option.color,
                                            boxShadow: active ? `0 0 6px ${option.color}` : 'none',
                                        }}
                                    />
                                    {option.label}
                                </button>
                            );
                        })}
                        {patternSortOptions.length > 0 && (
                            <label
                                htmlFor="task-pattern-sort"
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 7,
                                    border: sortPattern
                                        ? `1px solid ${
                                              patternSortOptions.find((option) => option.id === sortPattern)?.color ||
                                              '#EF4444'
                                          }88`
                                        : borders.hair2,
                                    background: sortPattern
                                        ? `${
                                              patternSortOptions.find((option) => option.id === sortPattern)?.color ||
                                              '#EF4444'
                                          }18`
                                        : 'transparent',
                                    borderRadius: 999,
                                    padding: '0 10px',
                                    minHeight: 31,
                                }}
                            >
                                <span
                                    style={{
                                        fontSize: 9,
                                        color: colors.subtle,
                                        fontFamily: fonts.mono,
                                        letterSpacing: '0.1em',
                                    }}
                                >
                                    {patternSortLabel.toUpperCase()}
                                </span>
                                <select
                                    id="task-pattern-sort"
                                    value={sortPattern || ''}
                                    onChange={(event) => {
                                        const next = event.target.value || null;

                                        onSortDimensionChange?.(null);
                                        onSortPendulumChange?.(null);
                                        onSortPatternChange?.(next);
                                    }}
                                    aria-label={patternSortLabel}
                                    style={{
                                        maxWidth: 210,
                                        border: 'none',
                                        outline: 'none',
                                        background: 'transparent',
                                        color: sortPattern ? colors.textPrimary : colors.muted,
                                        fontFamily: 'inherit',
                                        fontSize: 11,
                                        cursor: 'pointer',
                                    }}
                                >
                                    <option value="">{recommendedLabel}</option>
                                    {patternSortOptions.map((option) => (
                                        <option key={option.id} value={option.id} style={{ color: '#111' }}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </label>
                        )}
                        {pendulumSortOptions.length > 0 && (
                            <label
                                htmlFor="task-pendulum-sort"
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 7,
                                    border: sortPendulum ? '1px solid #A78BFA88' : borders.hair2,
                                    background: sortPendulum ? '#A78BFA18' : 'transparent',
                                    borderRadius: 999,
                                    padding: '0 10px',
                                    minHeight: 31,
                                }}
                            >
                                <span
                                    style={{
                                        fontSize: 9,
                                        color: colors.subtle,
                                        fontFamily: fonts.mono,
                                        letterSpacing: '0.1em',
                                    }}
                                >
                                    {pendulumSortLabel.toUpperCase()}
                                </span>
                                <select
                                    id="task-pendulum-sort"
                                    value={sortPendulum || ''}
                                    onChange={(event) => {
                                        const next = event.target.value || null;

                                        onSortDimensionChange?.(null);
                                        onSortPatternChange?.(null);
                                        onSortPendulumChange?.(next);
                                    }}
                                    aria-label={pendulumSortLabel}
                                    style={{
                                        maxWidth: 220,
                                        border: 'none',
                                        outline: 'none',
                                        background: 'transparent',
                                        color: sortPendulum ? colors.textPrimary : colors.muted,
                                        fontFamily: 'inherit',
                                        fontSize: 11,
                                        cursor: 'pointer',
                                    }}
                                >
                                    <option value="">{recommendedLabel}</option>
                                    {pendulumSortOptions.map((option) => (
                                        <option key={option.id} value={option.id} style={{ color: '#111' }}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </label>
                        )}
                    </div>
                    {sortHint && (
                        <div
                            style={{
                                marginLeft: 'auto',
                                maxWidth: 290,
                                color: colors.subtle,
                                fontSize: 11,
                                lineHeight: 1.45,
                                textAlign: 'right',
                            }}
                        >
                            {sortHint}
                        </div>
                    )}
                </div>
            )}

            {activeMonth && (
                <>
                    <div
                        className="dash-edit__task-drawer-head"
                        style={{
                            padding: '20px 24px',
                            background: colors.cardBgInner,
                            border: borders.hair2,
                            borderRadius: '14px 14px 0 0',
                            borderBottom: 'none',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}
                    >
                        <div>
                            <div
                                style={{
                                    fontSize: 11,
                                    color: colors.muted,
                                    letterSpacing: '0.18em',
                                    fontFamily: fonts.mono,
                                    marginBottom: 6,
                                }}
                            >
                                MONTH {String(activeMonth.idx).padStart(2, '0')} · ACTIVE
                            </div>
                            <div
                                style={{
                                    fontSize: 22,
                                    fontWeight: 500,
                                    color: colors.textPrimary,
                                    letterSpacing: '-0.02em',
                                }}
                            >
                                {activeMonth.theme}
                            </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                            <div
                                style={{
                                    fontSize: 12,
                                    color: colors.muted,
                                    fontFamily: fonts.mono,
                                    letterSpacing: '0.05em',
                                }}
                            >
                                {liveDone}/{activeMonth.total} done
                            </div>
                            <div
                                style={{
                                    width: 120,
                                    height: 4,
                                    background: 'rgba(255,255,255,0.06)',
                                    borderRadius: 2,
                                    position: 'relative',
                                }}
                            >
                                <div
                                    style={{
                                        position: 'absolute',
                                        inset: 0,
                                        width: `${(liveDone / activeMonth.total) * 100}%`,
                                        background: score.hex,
                                        borderRadius: 2,
                                        boxShadow: `0 0 6px ${score.hex}88`,
                                        transition: 'width .5s ease',
                                    }}
                                />
                            </div>
                            <div
                                style={{
                                    fontSize: 12,
                                    color: colors.textPrimary,
                                    fontFamily: fonts.mono,
                                    fontWeight: 500,
                                }}
                            >
                                {Math.round((liveDone / activeMonth.total) * 100)}%
                            </div>
                        </div>
                    </div>

                    <div
                        style={{
                            background: colors.cardBgInner,
                            border: borders.hair2,
                            borderTop: 'none',
                            borderRadius: '0 0 14px 14px',
                            overflow: 'hidden',
                        }}
                    >
                        <div
                            style={{
                                padding: '14px 24px',
                                borderBottom: borders.hair2,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 12,
                                color: colors.muted,
                            }}
                        >
                            <div
                                style={{
                                    width: 16,
                                    height: 16,
                                    borderRadius: '50%',
                                    border: '1.5px solid #84cc16',
                                    display: 'grid',
                                    placeItems: 'center',
                                    fontSize: 9,
                                    color: '#84cc16',
                                }}
                            >
                                ✓
                            </div>
                            <span style={{ fontSize: 13, letterSpacing: '-0.005em' }}>
                                {doneCount} completed earlier this month
                            </span>
                            <div style={{ flex: 1 }} />
                            <span
                                style={{
                                    fontSize: 11,
                                    color: colors.subtle,
                                    fontFamily: fonts.mono,
                                    letterSpacing: '0.1em',
                                }}
                            >
                                SHOW ALL ›
                            </span>
                        </div>

                        {todayTask && (
                            <TodayTaskDetail
                                t={todayTask}
                                score={score}
                                completing={false}
                                onMarkDone={handleMarkDone}
                            />
                        )}

                        {queued.map((t, i) => (
                            <QueuedTaskCard
                                key={t.n}
                                t={t}
                                isLast={i === queued.length - 1 && justDone.length === 0}
                                completing={false}
                                onMarkDone={handleMarkDone}
                            />
                        ))}

                        {justDone.map((t, i) => (
                            <DoneTaskRow key={`done-${t.n}`} t={t} isLast={i === justDone.length - 1} />
                        ))}

                        {!todayTask && queued.length === 0 && justDone.length === 0 && (
                            <div
                                style={{
                                    padding: '80px 24px',
                                    textAlign: 'center',
                                    fontSize: 16,
                                    color: colors.dim,
                                }}
                            >
                                All clear - see you tomorrow.
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default TaskList;
